<template>
  <div class="flex items-center justify-between">
    <div>
      <div class="text-sm font-medium">
        {{ getLocationText(allocation, mode, locale) }}
      </div>
      <div class="text-sm">
        {{
          formatDate(
            (showBookingDate ? booking : allocation)[mode === 'pickup' ? 'startDate' : 'endDate'],
          )
        }}
        <span class="text-obsidian-500">
          {{
            earlierTransactionDate
              ? `– ${earlierTransactionText ?? t('executedEarlier')}: ${formatDate(earlierTransactionDate)}`
              : ''
          }}
        </span>
      </div>
    </div>
    <div class="pr-4">
      <CVButton
        v-if="getProtocolFileUrl()"
        variant="ghost"
        :title="t('downloadProtocol')"
        @click.prevent="downloadProtocol"
        ><DownloadIcon class="h-4 w-4"
      /></CVButton>
      <CVButton
        v-if="!isImmutable"
        variant="ghost"
        :title="t('editLocationAndTime')"
        size="icon"
        @click.prevent="emit('edit')"
        ><PencilIcon class="h-4 w-4"
      /></CVButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Allocation } from '@/entities/allocations/allocation.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import CVButton from '../buttons/CVButton.vue';
import { DownloadIcon, PencilIcon } from 'lucide-vue-next';
import type { Booking } from '@/entities/bookings/booking.entity';
import { computed } from 'vue';
import { AllocationStatus } from '@carvia/ros-client-types';
import { getLocationText } from '@/utils/get-location-text.ts';

const { allocation, mode, booking } = defineProps<{
  booking: Booking;
  allocation: Allocation;
  mode: 'pickup' | 'dropoff';
  earlierTransactionDate?: string;
  earlierTransactionText?: string;
  showBookingDate?: boolean;
}>();

const emit = defineEmits<{
  (e: 'edit'): void;
}>();

const { t, locale } = useI18n();

const isImmutable = computed(() => {
  return (
    (mode === 'pickup' && allocation.status === AllocationStatus.HANDED_OVER) ||
    allocation.status === AllocationStatus.RETURNED
  );
});

const getProtocolFileUrl = () => {
  const { swapFromId, swapToId } = getSwapFromAndToId();
  return booking.carSwaps.find((swap) =>
    mode === 'pickup'
      ? swap.handedOverAllocationId === swapToId
      : swap.returnedAllocationId === swapFromId,
  )?.protocolFileUrl;
};

const downloadProtocol = () => {
  const protocol = getProtocolFileUrl();
  if (!protocol) return;
  window.location.href = protocol;
};

const getSwapFromAndToId = (): { swapFromId?: string; swapToId?: string } => {
  const relatedAllocationIndex =
    booking.allocations.findIndex((a) => a.id === allocation.id) + (mode === 'pickup' ? -1 : 1);
  return {
    swapFromId: mode === 'pickup' ? booking.allocations[relatedAllocationIndex]?.id : allocation.id,
    swapToId: mode === 'pickup' ? allocation.id : booking.allocations[relatedAllocationIndex]?.id,
  };
};
</script>

<i18n lang="json">
{
  "en": {
    "editLocationAndTime": "Edit location and time",
    "executedEarlier": "Conducted earlier"
  },
  "de": {
    "editLocationAndTime": "Ort und Zeit bearbeiten",
    "executedEarlier": "Früher durchgeführt"
  },
  "es": {
    "editLocationAndTime": "Editar lugar y hora",
    "executedEarlier": "Realizado antes"
  }
}
</i18n>
